/**
 * Vite plugins for RUN.world H5 apps.
 *
 * @module @series-inc/rundot-game-sdk/vite
 *
 * ## rundotGameLibrariesPlugin
 * Enables games to load common libraries (Phaser, React, Three.js)
 * from the RUN.world host app on mobile, or from CDN on web, instead of bundling them.
 *
 * Embedded libraries are enabled by default for optimal bundle size.
 * Set RUNDOT_GAME_DISABLE_EMBEDDED_LIBS=true to create standalone bundles.
 *
 * @example
 * ```typescript
 * // vite.config.ts
 * import { defineConfig } from 'vite';
 * import { rundotGameLibrariesPlugin } from '@series-inc/rundot-game-sdk/vite';
 *
 * export default defineConfig({
 *   plugins: [
 *     rundotGameLibrariesPlugin({
 *       appName: 'my-game',
 *     }),
 *   ],
 * });
 * ```
 *
 * ## rundotGamePlaygroundPlugin
 * Enables H5 apps to communicate with the real Firebase backend during local development.
 * Uses Google Sign-In for dev/staging authentication.
 *
 * ### Headless auth & credentials
 * For headless/CI playground login, set `RUNDOT_PLAYGROUND_KEY` in `.env` /
 * `.env.local` (normally minted by `rundot playground grant-access`) — no
 * `apiKey:` wiring needed. It is distinct from the deploy key (`RUNDOT_API_KEY`),
 * which the plugin never reads. The key is read dev-server-side only and is NEVER
 * serialized to the browser: the SDK fetches a short-lived custom token from a
 * localhost-only middleware. Keep the dev
 * server bound to localhost, and never commit the key. `RUNDOT_PLAYGROUND_KEY`
 * must contain a playground-scoped `pk_` key; the plugin never reads the deploy
 * key (`RUNDOT_API_KEY`, `rk_`).
 *
 * @example
 * ```typescript
 * // vite.config.ts
 * import { defineConfig } from 'vite';
 * import { rundotGamePlaygroundPlugin } from '@series-inc/rundot-game-sdk/vite';
 *
 * export default defineConfig({
 *   plugins: [
 *     rundotGamePlaygroundPlugin(),
 *   ],
 * });
 * ```
 *
 * The former name `rundotGameSandboxPlugin` still works as a deprecated alias. (@deprecated-alias)
 */

import { rundotGamePlaygroundPlugin } from './RundotGamePlaygroundPlugin'
import type { RundotGamePlaygroundPluginOptions } from './RundotGamePlaygroundPlugin'
import { warnRenamed } from '../deprecationWarning'

export { rundotGameLibrariesPlugin } from './RundotGameLibrariesPlugin'
export { cdnPlugin } from './cdnPlugin'
export { rundotGamePlaygroundPlugin } from './RundotGamePlaygroundPlugin'
export type { RundotGameLibrariesPluginOptions } from './RundotGameLibrariesPlugin'
export type { RundotGamePlaygroundPluginOptions } from './RundotGamePlaygroundPlugin'


// Multiplayer plugin
export { rundotMultiplayerPlugin } from './multiplayer/plugin'
export type { MultiplayerPluginOptions, DeterministicRoomClass } from './multiplayer/plugin'

// Storage check plugin
export { rundotStorageCheckPlugin } from './storageCheckPlugin'
export type {
  StorageCheckPluginOptions,
  StorageCheckSeverity,
} from './storageCheckPlugin'
export type {
  BlockedStorageApi,
  StorageViolation,
} from './storageLinter'


// @deprecated back-compat aliases — the local builder was renamed
// "sandbox" → "playground". Existing game vite.config.ts files import the old
// names; these warn-and-delegate, and are removed in the next major SDK version.
/** @deprecated Use `rundotGamePlaygroundPlugin`. */
export function rundotGameSandboxPlugin( // @deprecated-alias
  ...args: Parameters<typeof rundotGamePlaygroundPlugin>
): ReturnType<typeof rundotGamePlaygroundPlugin> {
  warnRenamed('rundotGameSandboxPlugin', 'rundotGamePlaygroundPlugin') // @deprecated-alias
  return rundotGamePlaygroundPlugin(...args)
}
/** @deprecated Use `rundotGamePlaygroundPlugin`. */
export function venusSandboxPlugin( // @deprecated-alias
  ...args: Parameters<typeof rundotGamePlaygroundPlugin>
): ReturnType<typeof rundotGamePlaygroundPlugin> {
  warnRenamed('venusSandboxPlugin', 'rundotGamePlaygroundPlugin') // @deprecated-alias
  return rundotGamePlaygroundPlugin(...args)
}
/** @deprecated Use `RundotGamePlaygroundPluginOptions`. */
export type RundotGameSandboxPluginOptions = RundotGamePlaygroundPluginOptions // @deprecated-alias
export { rundotGameLibrariesPlugin as venusLibrariesPlugin } from './RundotGameLibrariesPlugin'
